import apiClient from './client';
import type { Consultation, ConsultationWithMedications } from '@/types/consultation';

export interface CreateConsultationData {
  appointmentId: string;
  patientId: string;
  notes?: string;
  diagnosis?: string;
  medications?: {
    medicationId: string;
    durationDays: number;
    instructions?: string;
  }[];
}

class ConsultationsService {
  async create(data: CreateConsultationData): Promise<Consultation> {
    const response = await apiClient.post('/consultations', data);
    return response.data;
  }

  async getByAppointment(appointmentId: string): Promise<ConsultationWithMedications | null> {
    try {
      const response = await apiClient.get(`/consultations/appointment/${appointmentId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getByPatient(patientId: string): Promise<Consultation[]> {
    const response = await apiClient.get(`/consultations/patient/${patientId}`);
    return response.data;
  }

  async complete(id: string): Promise<Consultation> {
    const response = await apiClient.post(`/consultations/${id}/complete`);
    return response.data;
  }

  async update(id: string, data: { notes?: string; diagnosis?: string }): Promise<Consultation> {
    const response = await apiClient.put(`/consultations/${id}`, data);
    return response.data;
  }

  async updateMedications(
    id: string,
    medications: {
      id?: string;
      medicationId: string;
      durationDays: number;
      instructions?: string;
    }[]
  ): Promise<{ medications: any[] }> {
    const response = await apiClient.put(`/consultations/${id}/medications`, { medications });
    return response.data;
  }

  async cancel(id: string, reason: string): Promise<Consultation> {
    const response = await apiClient.post(`/consultations/${id}/cancel`, { reason });
    return response.data;
  }

  async reschedule(
    id: string,
    data: {
      reason: string;
      newStartUtc: string;
      newEndUtc: string;
    }
  ): Promise<{ message: string; oldConsultationId: string; newAppointment: any }> {
    const response = await apiClient.post(`/consultations/${id}/reschedule`, data);
    return response.data;
  }
}

export const consultationsService = new ConsultationsService();
